import { TranslateModule } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, computed, input, model, output, signal } from '@angular/core';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { DrawerModule } from 'primeng/drawer';
import { NgTemplateOutlet } from '@angular/common';

export type IoSidenavPosition = 'left' | 'right' | 'top' | 'bottom';
export type IoSidenavStatus = 'hidden' | 'collapsed' | 'opened';
export type IoSidenavBranding = 'logo' | 'icon' | 'none';

@Component({
  selector: 'io-sidenav',
  standalone: true,
  imports: [DrawerModule, TranslateModule, NgTemplateOutlet],
  templateUrl: './io-sidenav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IoSidenavComponent {
  /** Two-way binding for drawer visibility. */
  readonly visible = model(false);

  /** Fired when a nav item is clicked (after optional `item.command`). */
  readonly itemSelect = output<MenuItem>();

  readonly position = input<IoSidenavPosition>('left');
  readonly status = model<IoSidenavStatus>('opened');
  readonly items = input<MenuItem[]>([], { alias: 'model' });

  /** Index of the active item, or `null` for none. */
  readonly activeIndex = input<number | null>(null);

  readonly branding = input<IoSidenavBranding>('none');
  /** Main title (shown to the right of logo / icon). */
  readonly title = input('');
  /** Shown under the title. */
  readonly subtitle = input('');
  /** Hint line under the title block. */
  readonly menuHint = input('');

  readonly logoText = input('IO');
  readonly logoSrc = input('');
  readonly iconClass = input('pi pi-compass');
  readonly headerBackground = input('var(--p-primary-color)');
  readonly headerColor = input('var(--p-primary-contrast-color)');
  readonly openedWidth = input('20rem');
  readonly collapsedWidth = input('5.5rem');
  readonly modal = input(true);
  readonly dismissible = input(true);
  readonly blockScroll = input(false);
  readonly styleClass = input('');
  readonly embedded = input(false);

  /** Whether the drawer should render open. */
  readonly isVisible = computed(() => this.visible() && this.status() !== 'hidden');

  /** Whether we are in collapsed (icon-rail) mode. */
  readonly isCollapsed = computed(() => this.status() === 'collapsed');

  /** Inline style object forwarded to p-drawer. */
  readonly drawerStyle = computed<Record<string, string>>(() => ({
    '--io-sidenav-collapsed-width': this.collapsedWidth(),
    '--io-sidenav-opened-width': this.openedWidth(),
    '--io-sidenav-header-bg': this.headerBackground(),
    '--io-sidenav-header-fg': this.headerColor(),
  }));

  /** Merged styleClass for p-drawer. */
  readonly computedStyleClass = computed(() =>
    [
      'io-sidenav-drawer',
      this.isCollapsed() ? 'io-sidenav-drawer--collapsed' : 'io-sidenav-drawer--opened',
      this.styleClass(),
    ]
      .filter(Boolean)
      .join(' '),
  );

  readonly embeddedStyle = computed<Record<string, string>>(() => ({
    '--io-sidenav-collapsed-width': this.collapsedWidth(),
    '--io-sidenav-opened-width': this.openedWidth(),
    '--io-sidenav-header-bg': this.headerBackground(),
    '--io-sidenav-header-fg': this.headerColor(),
    width:
      this.status() === 'hidden' ? '0' : this.status() === 'collapsed' ? this.collapsedWidth() : this.openedWidth(),
  }));

  /** Close-arrow icon class based on position. */
  readonly closeArrowClass = computed(() => {
    const arrows: Record<IoSidenavPosition, string> = {
      right: 'pi pi-chevron-left',
      top: 'pi pi-chevron-down',
      bottom: 'pi pi-chevron-up',
      left: 'pi pi-chevron-left',
    };
    return arrows[this.position()];
  });

  /** Whether any menu items have icons (needed for collapsed rail). */
  readonly hasIcons = computed(() => this.items().some(item => !!item.icon));

  /** Whether the branding mark should show. */
  readonly showBranding = computed(() => this.branding() !== 'none');

  /** PrimeNG close icon is disabled; close control is the footer button. */
  readonly showCloseIcon = false;

  onMouseEnter(): void {
    // Hover behavior disabled by design.
  }

  onMouseLeave(): void {
    // Hover behavior disabled by design.
  }

  onItemClick(item: MenuItem): void {
    const event: MenuItemCommandEvent = { item, originalEvent: undefined };
    item.command?.(event);
    this.itemSelect.emit(item);
  }

  closeDrawer(): void {
    if (this.status() === 'collapsed') {
      this.status.set('opened');
      this.visible.set(true);
      return;
    }

    this.status.set('collapsed');
    this.visible.set(true);
  }

  isItemActive(index: number): boolean {
    return this.activeIndex() === index;
  }

  /** Whether a count badge should render for this row (expanded rail only). */
  showItemBadge(item: MenuItem): boolean {
    return !this.isCollapsed() && !item.separator && !!item.badge?.trim();
  }
}
